/**
 * ID3v2 PRIV frame reader for metadata technique.
 *
 * Reads the PRIV frame with owner "com.suno-covert.payload" from an MP3,
 * then base64-decodes the data to get the raw stego blob.
 *
 * This is decode-only in the browser — encoding requires the server
 * to inject frames via the /stego/embed endpoint.
 */

import { fromBase64 } from "../../crypto/utils.js";

export const PRIV_OWNER = "com.suno-covert.payload";

/**
 * Extract stego blob from ID3v2 PRIV frame in an MP3 buffer.
 * Returns null if no matching PRIV frame is found.
 */
export function extractMetadataPayload(
  mp3: Uint8Array
): Uint8Array | null {
  // ID3v2 header: "ID3" + version(2) + flags(1) + size(4 syncsafe)
  if (mp3.length < 10) return null;
  if (mp3[0] !== 0x49 || mp3[1] !== 0x44 || mp3[2] !== 0x33) return null; // "ID3"

  const version = mp3[3]!;
  if (version !== 3 && version !== 4) return null; // Only v2.3 and v2.4

  const flags = mp3[5]!;
  const tagSize = decodeSyncsafe(mp3, 6);
  const tagEnd = 10 + tagSize;

  let offset = 10;

  // Skip extended header if present (bit 6 of flags)
  if (flags & 0x40) {
    if (offset + 4 > tagEnd) return null;
    const extSize = version === 4
      ? decodeSyncsafe(mp3, offset)
      : readUint32BE(mp3, offset);
    offset += version === 4 ? extSize : 4 + extSize;
  }

  // Iterate frames
  while (offset + 10 <= tagEnd) {
    const frameId = String.fromCharCode(
      mp3[offset]!,
      mp3[offset + 1]!,
      mp3[offset + 2]!,
      mp3[offset + 3]!
    );

    // Padding check (null bytes mean end of frames)
    if (frameId === "\0\0\0\0") break;

    const frameSize = version === 4
      ? decodeSyncsafe(mp3, offset + 4)
      : readUint32BE(mp3, offset + 4);

    // Frame flags: 2 bytes at offset+8
    const frameDataStart = offset + 10;
    const frameDataEnd = frameDataStart + frameSize;

    if (frameId === "PRIV" && frameDataEnd <= tagEnd) {
      // PRIV frame: owner string (null-terminated) + data
      const frameData = mp3.slice(frameDataStart, frameDataEnd);
      const nullIdx = frameData.indexOf(0);
      if (nullIdx >= 0) {
        const owner = new TextDecoder().decode(frameData.slice(0, nullIdx));
        if (owner === PRIV_OWNER) {
          const privData = frameData.slice(nullIdx + 1);
          // Data is base64-encoded
          const b64 = new TextDecoder().decode(privData);
          return fromBase64(b64);
        }
      }
    }

    offset = frameDataEnd;
  }

  return null;
}

/**
 * Extract the first APIC (album art) frame image data from an MP3 buffer.
 * Returns the raw image bytes (PNG/JPEG), or null if no APIC frame is found.
 */
export function extractApicImage(mp3: Uint8Array): Uint8Array | null {
  if (mp3.length < 10) return null;
  if (mp3[0] !== 0x49 || mp3[1] !== 0x44 || mp3[2] !== 0x33) return null;

  const version = mp3[3]!;
  if (version !== 3 && version !== 4) return null;

  const flags = mp3[5]!;
  const tagSize = decodeSyncsafe(mp3, 6);
  const tagEnd = 10 + tagSize;

  let offset = 10;

  // Skip extended header if present
  if (flags & 0x40) {
    if (offset + 4 > tagEnd) return null;
    const extSize = version === 4
      ? decodeSyncsafe(mp3, offset)
      : readUint32BE(mp3, offset);
    offset += version === 4 ? extSize : 4 + extSize;
  }

  while (offset + 10 <= tagEnd) {
    const frameId = String.fromCharCode(
      mp3[offset]!,
      mp3[offset + 1]!,
      mp3[offset + 2]!,
      mp3[offset + 3]!
    );

    if (frameId === "\0\0\0\0") break;

    const frameSize = version === 4
      ? decodeSyncsafe(mp3, offset + 4)
      : readUint32BE(mp3, offset + 4);

    const frameDataStart = offset + 10;
    const frameDataEnd = frameDataStart + frameSize;

    if (frameId === "APIC" && frameDataEnd <= tagEnd) {
      const frameData = mp3.slice(frameDataStart, frameDataEnd);
      // APIC frame layout:
      //   [0]            text encoding byte
      //   [1..null]      MIME type (null-terminated)
      //   [null+1]       picture type byte
      //   [null+2..null] description (null-terminated)
      //   [rest]         raw image data

      let pos = 1; // skip text encoding byte

      // Find null terminator after MIME type
      while (pos < frameData.length && frameData[pos] !== 0) pos++;
      if (pos >= frameData.length) { offset = frameDataEnd; continue; }
      pos++; // skip null

      pos++; // skip picture type byte

      // Skip description (null-terminated)
      while (pos < frameData.length && frameData[pos] !== 0) pos++;
      if (pos >= frameData.length) { offset = frameDataEnd; continue; }
      pos++; // skip null

      // Remaining bytes are the image data
      if (pos < frameData.length) {
        return frameData.slice(pos);
      }
    }

    offset = frameDataEnd;
  }

  return null;
}

/** Decode a 4-byte syncsafe integer (ID3v2). */
function decodeSyncsafe(buf: Uint8Array, offset: number): number {
  return (
    ((buf[offset]! & 0x7f) << 21) |
    ((buf[offset + 1]! & 0x7f) << 14) |
    ((buf[offset + 2]! & 0x7f) << 7) |
    (buf[offset + 3]! & 0x7f)
  );
}

/** Read a big-endian uint32. */
function readUint32BE(buf: Uint8Array, offset: number): number {
  return (
    ((buf[offset]! << 24) |
      (buf[offset + 1]! << 16) |
      (buf[offset + 2]! << 8) |
      buf[offset + 3]!) >>>
    0
  );
}
